import { timezoneCountries } from './timezoneCountries'
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js'

export type { CountryCode }

export type PhoneCountry = Readonly<{ code: CountryCode; name: string; dialCode: string; flag: string }>

/** A country's flag, built from its two letters as Unicode regional indicators. */
export function flagOf(code: string) {
  return String.fromCodePoint(...[...code.toUpperCase()].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65))
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en-GB'], { type: 'region' })
  } catch {
    return null
  }
})()

/** Every country with a dialling code, by name, with its flag. */
export const phoneCountries: readonly PhoneCountry[] = getCountries()
  .map((code) => ({ code, name: regionNames?.of(code) ?? code, dialCode: `+${getCountryCallingCode(code)}`, flag: flagOf(code) }))
  .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'))

const known = new Set<string>(getCountries())
export const isPhoneCountry = (code: string | null | undefined): code is CountryCode => Boolean(code && known.has(code))

export function countryOf(code: CountryCode): PhoneCountry {
  return phoneCountries.find((country) => country.code === code) ?? { code, name: code, dialCode: `+${getCountryCallingCode(code)}`, flag: flagOf(code) }
}

/**
 * The international form to store (for example +447911123456), when the
 * number typed is a real number for the chosen country. A UK "07911 123456"
 * becomes +447911123456. A number typed with its own + code uses that code.
 */
export function toInternational(national: string, country: CountryCode): string | null {
  const typed = national.trim()
  if (!typed) return null
  const parsed = parsePhoneNumberFromString(typed, country)
  return parsed && parsed.isValid() ? parsed.number : null
}

/** Whether a stored or typed number is a real phone number (with its + code). */
export function isValidInternational(value: string) {
  return value.trim().startsWith('+') && isValidPhoneNumber(value.trim())
}

/** A number written with its own country code: +34… or the 0034… way of dialling abroad. */
export const hasOwnCountryCode = (typed: string) => /^\s*(\+|00)/.test(typed)

/**
 * Splits a number into its country and the rest, to show it for editing.
 * Codes shared by several countries (+44 is also Guernsey, Jersey and the
 * Isle of Man; +1 is the US, Canada and the Caribbean) keep the country
 * already chosen when it uses the same code, so the flag doesn't jump.
 */
export function splitNumber(value: string, fallback: CountryCode): { country: CountryCode; national: string } {
  const typed = value.trim()
  const parsed = hasOwnCountryCode(typed) ? parsePhoneNumberFromString(typed.replace(/^00/, '+')) : undefined
  if (parsed?.country) {
    const country = parsed.countryCallingCode === getCountryCallingCode(fallback) ? fallback : parsed.country
    return { country, national: parsed.formatNational() }
  }
  return { country: fallback, national: value }
}

/** The country an event is in, from its time zone (Europe/Madrid → Spain), if the zone belongs to one. */
export function countryForTimezone(timezone: string | null | undefined): CountryCode | null {
  const code = timezone ? timezoneCountries[timezone] : undefined
  return isPhoneCountry(code) ? code : null
}

const countriesByCallingCode = new Map<string, CountryCode[]>()
for (const code of getCountries()) {
  const calling = getCountryCallingCode(code)
  countriesByCallingCode.set(calling, [...(countriesByCallingCode.get(calling) ?? []), code])
}
// Codes several countries share go to the country that owns the numbering plan.
const mainCountry: Readonly<Record<string, CountryCode>> = { '1': 'US', '7': 'RU', '44': 'GB', '47': 'NO', '61': 'AU', '212': 'MA', '262': 'RE', '290': 'SH', '358': 'FI', '590': 'GP', '599': 'CW' }

/**
 * A country code typed at the start of a number (+34 or 0034), once it is
 * complete, and the rest of what was typed. Codes never start with another
 * code, so the first complete one is the only one.
 */
export function leadingCountryCode(typed: string): { callingCode: string; rest: string } | null {
  const match = /^\s*(?:\+|00)\s*([\d\s]*)(.*)$/s.exec(typed)
  if (!match) return null
  const digits = (match[1] ?? '').replace(/\s/g, '')
  for (let length = 1; length <= 3 && length <= digits.length; length += 1) {
    const callingCode = digits.slice(0, length)
    if (countriesByCallingCode.has(callingCode)) {
      // Keep the rest exactly as typed, after the code's digits.
      let seen = 0
      let index = 0
      const raw = (match[1] ?? '') + (match[2] ?? '')
      while (index < raw.length && seen < length) {
        if (/\d/.test(raw[index] ?? '')) seen += 1
        index += 1
      }
      return { callingCode, rest: raw.slice(index).replace(/^\s+/, '') }
    }
  }
  return null
}

/** The country for a typed code: the one already chosen if it shares the code, otherwise the code's own country. */
export function countryForCallingCode(callingCode: string, current: CountryCode): CountryCode | null {
  const countries = countriesByCallingCode.get(callingCode)
  if (!countries?.length) return null
  if (countries.includes(current)) return current
  return mainCountry[callingCode] ?? countries[0] ?? null
}

/** A stored number as people read it (+44 7911 123456); anything else as it was typed. */
export function formatPhone(value: string | null | undefined) {
  if (!value) return ''
  const parsed = value.trim().startsWith('+') ? parsePhoneNumberFromString(value.trim()) : undefined
  return parsed?.isValid() ? parsed.formatInternational() : value
}
